import { supabase } from 'utils/supabase'

class AiProgressRealtimeService {
    constructor() {
        this.channels = new Map() // Track active channels
        this.listeners = new Map() // Track listeners per case
        this.cleanupOnUnload = this.cleanupOnUnload.bind(this)
        this.initPageUnloadCleanup()
    }

    /**
     * Initialize page unload cleanup to close all channels on refresh/navigation
     */
    initPageUnloadCleanup() {
        // Cleanup on page unload/refresh
        if (typeof window !== 'undefined') {
            window.addEventListener('beforeunload', this.cleanupOnUnload)
            window.addEventListener('pagehide', this.cleanupOnUnload)
        }
    }

    /**
     * Cleanup all channels on page unload
     */
    cleanupOnUnload() {
        this.unsubscribeAll()
    }

    /**
     * Close a channel and clean up resources
     * @param {string} channelName - Channel name
     * @param {string} reason - Reason for closing (for logging)
     */
    closeChannel(channelName, reason = 'manual') {
        const channel = this.channels.get(channelName)
        if (channel) {
            try {
                supabase.removeChannel(channel)
                console.log(`[AiProgressRealtime] 🔒 Channel closed: ${channelName} (reason: ${reason})`)
            } catch (error) {
                console.error(`[AiProgressRealtime] ❌ Error closing channel ${channelName}:`, error)
            }
        }
        this.channels.delete(channelName)
        this.listeners.delete(channelName)
    }

    /**
     * Notify all listeners for a channel
     * @param {string} channelName - Channel name
     * @param {Object} progress - Progress data
     * @returns {boolean} - Returns true if status is completed/failed
     */
    notifyListeners(channelName, progress) {
        const listeners = this.listeners.get(channelName) || []
        
        listeners.forEach(listener => {
            try {
                listener({
                    stage: progress.stage,
                    stageProgress: progress.stage_progress,
                    stage_progress: progress.stage_progress, // Support both formats
                    overallProgress: progress.overall_progress,
                    overall_progress: progress.overall_progress, // Support both formats
                    status: progress.status,
                    message: progress.message,
                })
            } catch (error) {
                console.error('[AiProgressRealtime] Error in progress callback:', error)
            }
        })

        // Return true if status indicates completion
        return progress.status === 'completed' || progress.status === 'failed'
    }

    /**
     * Subscribe to AI summary progress updates for a specific case
     * Uses Supabase Realtime postgres_changes to listen for database updates
     * @param {number} caseId - Case ID
     * @param {number} mediatorId - Mediator ID
     * @param {Function} callback - Callback function to receive progress updates
     * @returns {Function} Unsubscribe function
     */
    subscribeToProgress(caseId, mediatorId, callback) {
        if (!caseId || !mediatorId) {
            console.warn('AiProgressRealtimeService: caseId and mediatorId are required')
            return () => {}
        }

        const channelName = `ai-progress-${caseId}-${mediatorId}`
        
        // If already subscribed, add callback to existing listeners
        if (this.channels.has(channelName)) {
            const listeners = this.listeners.get(channelName) || []
            // Prevent duplicate callbacks
            if (listeners.includes(callback)) {
                console.warn(`[AiProgressRealtime] ⚠️ Callback already registered for ${channelName}`)
                return () => {}
            }
            listeners.push(callback)
            this.listeners.set(channelName, listeners)
            
            return () => {
                const currentListeners = this.listeners.get(channelName) || []
                const filtered = currentListeners.filter(l => l !== callback)
                if (filtered.length === 0) {
                    this.closeChannel(channelName, 'no listeners')
                } else {
                    this.listeners.set(channelName, filtered)
                }
            }
        }

        // Create new channel subscription using postgres_changes
        console.log(`[AiProgressRealtime] 🔔 Subscribing to progress updates for case ${caseId}, mediator ${mediatorId}`)
        
        // Use proper filter syntax for Supabase Realtime
        // Filter format: column=operator.value
        // For multiple conditions, we filter on case_id and check mediator_id in callback for privacy
        const channel = supabase.channel(channelName)
            .on('postgres_changes', {
                event: 'UPDATE',
                schema: 'public',
                table: 'ai_summary_progress',
                filter: `case_id=eq.${caseId}`
            }, (payload) => {
                const progress = payload.new
                
                // Additional privacy check: verify mediator_id matches
                if (progress && progress.mediator_id === mediatorId) {
                    console.log(`[AiProgressRealtime] 📨 Progress update received:`, {
                        stage: progress.stage,
                        stageProgress: progress.stage_progress,
                        overallProgress: progress.overall_progress,
                        status: progress.status,
                        message: progress.message,
                    })
                    
                    const isComplete = this.notifyListeners(channelName, progress)

                    // Auto-close channel if completed or failed
                    if (isComplete) {
                        console.log(`[AiProgressRealtime] ✅ Progress ${progress.status}, closing channel`)
                        // Delay cleanup slightly to ensure final callback is processed
                        setTimeout(() => {
                            this.closeChannel(channelName, `status: ${progress.status}`)
                        }, 100)
                    }
                }
            })
            .on('postgres_changes', {
                event: 'INSERT',
                schema: 'public',
                table: 'ai_summary_progress',
                filter: `case_id=eq.${caseId}`
            }, (payload) => {
                const progress = payload.new
                
                // Additional privacy check: verify mediator_id matches
                if (progress && progress.mediator_id === mediatorId) {
                    console.log(`[AiProgressRealtime] 📨 Initial progress insert:`, {
                        stage: progress.stage,
                        status: progress.status,
                    })
                    
                    const isComplete = this.notifyListeners(channelName, progress)

                    // Auto-close channel if already completed/failed (edge case)
                    if (isComplete) {
                        setTimeout(() => {
                            this.closeChannel(channelName, `status: ${progress.status}`)
                        }, 100)
                    }
                }
            })
            .subscribe((status, err) => {
                console.log(`[AiProgressRealtime] Channel ${channelName} status:`, status, err ? `(error: ${err.message})` : '')
                
                if (status === 'SUBSCRIBED') {
                    console.log(`[AiProgressRealtime] ✅ Successfully subscribed to progress updates for case ${caseId}, mediator ${mediatorId}`)
                } else if (status === 'CHANNEL_ERROR') {
                    console.error(`[AiProgressRealtime] ❌ Channel error for ${channelName}:`, err)
                    // Close channel on error
                    setTimeout(() => {
                        this.closeChannel(channelName, 'channel error')
                    }, 100)
                } else if (status === 'TIMED_OUT') {
                    console.warn(`[AiProgressRealtime] ⏱️ Channel subscription timed out: ${channelName}`)
                    // Close channel on timeout
                    setTimeout(() => {
                        this.closeChannel(channelName, 'timeout')
                    }, 100)
                } else if (status === 'CLOSED') {
                    console.warn(`[AiProgressRealtime] 🔒 Channel closed: ${channelName}`)
                    // Clean up internal state when channel is closed externally
                    this.channels.delete(channelName)
                    this.listeners.delete(channelName)
                }
            })

        // Store channel and listeners
        this.channels.set(channelName, channel)
        this.listeners.set(channelName, [callback])

        // Return unsubscribe function
        return () => {
            this.closeChannel(channelName, 'unsubscribe')
        }
    }

    /**
     * Unsubscribe from progress updates
     * @param {string} channelName - Channel name
     * @param {Function} callback - Optional specific callback to remove
     */
    unsubscribe(channelName, callback = null) {
        if (callback) {
            // Remove specific callback
            const listeners = this.listeners.get(channelName) || []
            const filtered = listeners.filter(l => l !== callback)
            
            if (filtered.length === 0) {
                // No more listeners, close channel
                this.closeChannel(channelName, 'no listeners')
            } else {
                this.listeners.set(channelName, filtered)
            }
        } else {
            // Close entire channel
            this.closeChannel(channelName, 'unsubscribe all')
        }
    }

    /**
     * Unsubscribe from all channels
     */
    unsubscribeAll() {
        const channelNames = Array.from(this.channels.keys())
        channelNames.forEach(channelName => {
            this.closeChannel(channelName, 'unsubscribe all')
        })
        console.log(`[AiProgressRealtime] 🧹 Unsubscribed from all channels (${channelNames.length} total)`)
    }

    /**
     * Cleanup method to be called when service is no longer needed
     * Removes event listeners and closes all channels
     */
    destroy() {
        if (typeof window !== 'undefined') {
            window.removeEventListener('beforeunload', this.cleanupOnUnload)
            window.removeEventListener('pagehide', this.cleanupOnUnload)
        }
        this.unsubscribeAll()
    }
}

// Export singleton instance
const service = new AiProgressRealtimeService()

// Cleanup on module unload (if supported)
if (typeof window !== 'undefined' && window.addEventListener) {
    // Additional cleanup on visibility change (optional)
    document.addEventListener('visibilitychange', () => {
        if (document.hidden) {
            // Page is hidden, but don't close channels yet
            // Channels will be closed on beforeunload/pagehide
        }
    })
}

export default service
